import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../utils/axios';

// The fee schedule, and where this organisation stands on it.
//
// Prices arrive in CENTS and are formatted at the point of display. GST is
// added at checkout, so a pre-formatted figure from the server would be wrong
// twice over — once for the currency and once for the tax.

export interface Plan {
  slug: string;
  name: string;
  minWorkers: number;
  maxWorkers: number | null;
  band: string;
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
  annualSavingCents: number | null;
  quoteOnly: boolean;
  /** The band this organisation's head count already falls in. */
  current: boolean;
}

export interface Workers {
  accountHolder: number;
  ownStaff: number;
  contractors: number;
  subWorkers: number;
  total: number;
}

export interface PlansResponse {
  workers: Workers | null;
  accountState: 'subscribed' | 'linked' | 'free' | null;
  plans: Plan[];
  /** Present once the account has chosen; null means never asked. */
  planChoice?: PlanChoice | null;
  /**
   * What they are PAYING for, which is not the same question as which band
   * their head count falls in. Null on a free profile.
   */
  subscribedPlan?: {
    slug: string;
    name: string;
    band: string;
    monthlyPriceCents: number;
    annualPriceCents: number;
  } | null;
  /** Paying for a band smaller than the team. */
  overBand?: boolean;
}

const unwrap = <T,>(res: any): T => (res?.data?.data ?? res?.data) as T;

export const billingKeys = { plans: ['billing', 'plans'] as const };

/**
 * The fee schedule for the PUBLIC pricing page.
 *
 * A separate endpoint rather than the authenticated one with a fallback: this
 * page is rendered for people with no account, and an anonymous request to the
 * signed-in route would 401 on every load. It returns the bands and nothing
 * about anybody, so there is no head count and no band marked as yours.
 */
export function usePublicPlans() {
  return useQuery({
    queryKey: ['billing', 'plans', 'public'],
    queryFn: async (): Promise<Plan[]> => {
      const res = unwrap<{ plans: Plan[] }>(await axios.get('/v1/billing/plans/public'));
      return Array.isArray(res?.plans) ? res.plans : [];
    },
    staleTime: 600_000,
    retry: 1,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: billingKeys.plans,
    queryFn: async (): Promise<PlansResponse> => unwrap<PlansResponse>(await axios.get('/v1/billing/plans')),
    staleTime: 300_000,
  });
}

export interface SubscriptionRequest {
  id: number;
  planSlug: string;
  billingPeriod: 'monthly' | 'annual';
  workerCount: number | null;
  note: string | null;
  status: 'open' | 'actioned' | 'declined';
  createdAt: string;
}

export function useMyPlanRequests() {
  return useQuery({
    queryKey: ['billing', 'requests', 'mine'],
    queryFn: async (): Promise<SubscriptionRequest[]> => {
      const rows = unwrap<SubscriptionRequest[]>(await axios.get('/v1/billing/requests/mine'));
      return Array.isArray(rows) ? rows : [];
    },
    staleTime: 60_000,
  });
}

/**
 * Ask to be put on a plan.
 *
 * Not a checkout. Nothing here moves money — it records which plan and period
 * the customer chose and notifies TWC, which is the honest thing to offer
 * while four pricing questions are still open.
 */
export function useRequestPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      planSlug: string;
      billingPeriod?: 'monthly' | 'annual';
      note?: string;
    }) => unwrap(await axios.post('/v1/billing/requests', input)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing', 'requests', 'mine'] });
    },
  });
}

// ── admin ───────────────────────────────────────────────────────────────────

export function usePlanRequests(status: 'open' | 'actioned' | 'declined' | 'all' = 'open') {
  return useQuery({
    queryKey: ['billing', 'requests', status],
    queryFn: async (): Promise<any[]> => {
      const rows = unwrap<any[]>(await axios.get('/v1/billing/requests', { params: { status } }));
      return Array.isArray(rows) ? rows : [];
    },
    staleTime: 30_000,
  });
}

export function useSetPlanRequestStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: 'open' | 'actioned' | 'declined' }) =>
      unwrap(await axios.patch(`/v1/billing/requests/${id}`, { status })),
    // Every tab is a filtered view of the same rows, so a move between states
    // has to refresh them all — not just the one being looked at.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing', 'requests'] }),
  });
}

export interface PlanChoice {
  /** null IS the Free choice — an answer, not an absence. */
  slug: string | null;
  name: string;
  billingPeriod: 'monthly' | 'annual' | null;
  status: string;
  trialEndsAt: string | null;
  chosenAt: string;
}

/**
 * Record the plan chosen at the end of onboarding.
 *
 * Takes no payment. Free records the decision; a paid choice starts the trial
 * and files a request into the admin queue. Omitting planSlug means Free.
 */
/**
 * Start a payment.
 *
 * Returns a Stripe Checkout URL for the browser to follow. Enterprise is
 * refused server-side — a plan priced per organisation has no price to check
 * out against, so it keeps the request flow.
 */
export function useCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      planSlug: string;
      billingPeriod?: 'monthly' | 'annual';
      // `url` is absent when the card is already on file — the plan is swapped
      // on the existing subscription instead of starting a second one.
    }): Promise<{ url?: string | null; changed?: boolean; alreadyOnPlan?: boolean }> =>
      unwrap(await axios.post('/v1/billing/checkout-session', input)),
    // A swap changes the subscription without leaving the app, so nothing else
    // refetches it — the subscription page kept showing the old plan until the
    // browser was reloaded by hand. A checkout that DOES leave for Stripe comes
    // back through the sync on return, so invalidating here is harmless there.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });
}

/**
 * Pull the subscription from Stripe now.
 *
 * Called when a checkout sends somebody back. Webhooks are the source of
 * truth, but they are asynchronous and cannot reach a laptop at all — without
 * this the page they land on still says Free, which looks exactly like failure.
 */
export function useSyncSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await axios.post('/v1/billing/sync', {})),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });
}

/** Stripe's hosted portal: card, invoices, cancelling. */
export function useBillingPortal() {
  return useMutation({
    mutationFn: async (): Promise<{ url: string }> =>
      unwrap<{ url: string }>(await axios.post('/v1/billing/portal-session', {})),
  });
}

export function useSelectPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      planSlug?: string;
      billingPeriod?: 'monthly' | 'annual';
      note?: string;
    }): Promise<PlanChoice> => unwrap<PlanChoice>(await axios.post('/v1/billing/select-plan', input)),
    onSuccess: () => {
      // The subscription page and the plan grid both describe the account's
      // position, and it just moved.
      queryClient.invalidateQueries({ queryKey: billingKeys.plans });
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}
